import { useEffect, useState } from "react";
import { Loading } from "@/components/Loading";
import { Link, useNavigate } from "react-router-dom";
import { useBackOrigin, type BackOrigin } from "@/hooks/useBackTo";
import { SortableTH } from "@/components/SortableTH";
import { Pagination } from "@/components/Pagination";
import { usePagedList } from "@/hooks/usePagedList";
import { useColumnSort } from "@/hooks/useColumnSort";
import { StatusPill, MERCHANT_STATUS_TONES } from "@/components/StatusPill";
import { Notice } from "@/components/Notice";
import { Dash } from "@/components/Dash";
import { SearchInput } from "@/components/SearchInput";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, CreditCard, LayoutGrid, MapPin, Phone, Table2, User } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { merchants, type MerchantListItem, type MerchantSummary } from "@/features/merchants/api/merchants.api";
import { formatDate } from "@/utils/dates";

type Bucket = "live" | "approved" | "inactive" | "closed";
type SortKey = "dbaName" | "mid" | "status" | "approvedDate" | "ownerName" | "lastVolume" | "avgVolume";
type ViewMode = "cards" | "table";

const VIEW_MODE_KEY = "merchants:viewMode";

const COLUMN_MAP: Record<SortKey, string> = {
  dbaName: "dbaName",
  mid: "mid",
  status: "status",
  approvedDate: "approvedDate",
  ownerName: "ownerName",
  lastVolume: "lastVolume",
  avgVolume: "avgVolume",
};

const SORT_OPTIONS: { value: string; label: string; col: string; dir: "asc" | "desc" }[] = [
  { value: "dbaName:asc", label: "Sort: DBA (A→Z)", col: "dbaName", dir: "asc" },
  { value: "mid:asc", label: "Sort: MID", col: "mid", dir: "asc" },
  { value: "status:asc", label: "Sort: Status", col: "status", dir: "asc" },
  { value: "ownerName:asc", label: "Sort: Owner (A→Z)", col: "ownerName", dir: "asc" },
  { value: "lastVolume:desc", label: "Sort: Last volume (high→low)", col: "lastVolume", dir: "desc" },
  { value: "avgVolume:desc", label: "Sort: Avg volume (high→low)", col: "avgVolume", dir: "desc" },
];

const BUCKET_STATUS: Record<Bucket, string> = {
  live: "Live",
  approved: "Approved",
  inactive: "Inactive",
  closed: "Closed",
};

export default function MerchantsPage() {
  const navigate = useNavigate();
  const origin = useBackOrigin();
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    return saved === "table" || saved === "cards" ? saved : "cards";
  });
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const [summary, setSummary] = useState<MerchantSummary | null>(null);

  const { data, loading, loaded, error, forbidden, status, keyword, sortBy, sortDir, setKeyword, setStatus, setPage, setSort } =
    usePagedList<MerchantListItem>(merchants.list, {
      initialSortBy: "dbaName",
      initialSortDir: "asc",
      syncUrl: true,
    });
  const rows = data.items;

  useEffect(() => {
    let cancelled = false;
    merchants.summary()
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { /* tiles just won't show until summary loads */ });
    return () => { cancelled = true; };
  }, []);

  const { thProps } = useColumnSort<SortKey>(COLUMN_MAP, sortBy, sortDir, setSort);

  const activeBucket: Bucket | null =
    (Object.keys(BUCKET_STATUS) as Bucket[]).find((b) => BUCKET_STATUS[b] === status) ?? null;

  function toggleBucket(b: Bucket) {
    const s = BUCKET_STATUS[b];
    setStatus(status === s ? "" : s);
  }

  const statusFilter = status || "All";

  function pickStatusFilter(v: string) {
    setStatus(v === "All" ? "" : v);
  }

  const tileCounts = summary
    ? { live: summary.live, approved: summary.approved, inactive: summary.inactive, closed: summary.closed }
    : { live: 0, approved: 0, inactive: 0, closed: 0 };
  const tileTotal = summary?.total ?? data.totalCount;

  const sortValue = `${sortBy}:${sortDir}`;
  const sortIsCustom = !SORT_OPTIONS.some((o) => o.value === sortValue);

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Merchants</h1>
        <p className="text-sm text-slate-500 mt-1">
          {`${data.totalCount.toLocaleString()} merchants on your book.`}
        </p>
      </header>

      {forbidden && (
        <Notice tone="warn">
          You don't have permission to view merchants. Ask an admin to
          assign you a role that grants permission <code>20.05</code>
          (View only relevant Merchants).
        </Notice>
      )}

      {error && !forbidden && <Notice tone="error">{error}</Notice>}

      {!forbidden && !error && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {STATUS_TILES.map((t) => (
              <StatusTile
                key={t.bucket}
                label={t.label}
                count={tileCounts[t.bucket]}
                total={tileTotal}
                dot={t.dot}
                ring={t.ring}
                tint={t.tint}
                active={activeBucket === t.bucket}
                onClick={() => toggleBucket(t.bucket)}
              />
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(v) => { if (v) setViewMode(v as ViewMode); }}
              aria-label="Display mode"
              className="h-9 shrink-0 gap-0.5 rounded-md border border-input bg-muted/60 p-0.5 shadow-sm"
            >
              <ToggleGroupItem
                value="cards"
                className="h-8 gap-1.5 px-3 text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-slate-900 data-[state=on]:shadow-sm"
              >
                <LayoutGrid />
                Cards
              </ToggleGroupItem>
              <ToggleGroupItem
                value="table"
                className="h-8 gap-1.5 px-3 text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-slate-900 data-[state=on]:shadow-sm"
              >
                <Table2 />
                Table
              </ToggleGroupItem>
            </ToggleGroup>

            <div className="ml-auto flex flex-wrap items-center gap-3">
              <UISelect value={statusFilter} onValueChange={pickStatusFilter}>
                <SelectTrigger className="w-auto min-w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All statuses</SelectItem>
                  {STATUS_TILES.map((t) => <SelectItem key={t.label} value={t.label}>{t.label}</SelectItem>)}
                </SelectContent>
              </UISelect>

              <UISelect value={sortValue} onValueChange={(v) => {
                const opt = SORT_OPTIONS.find((o) => o.value === v);
                if (opt) setSort(opt.col, opt.dir);
              }}>
                <SelectTrigger className="w-auto min-w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  {sortIsCustom && <SelectItem value={sortValue}>Sort: custom</SelectItem>}
                </SelectContent>
              </UISelect>

              <SearchInput
                value={keyword}
                onSearch={setKeyword}
                placeholder="Search MID, DBA, legal name, city, contact…"
                className="w-96 max-w-full"
              />
            </div>
          </div>

          {loading && !loaded ? (
            <Loading>Loading merchants…</Loading>
          ) : (
            <div className={cn("transition-opacity", loading && "opacity-60")}>
            {rows.length === 0 ? (
            <Notice tone="info">No merchants match the current filters.</Notice>
          ) : viewMode === "cards" ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {rows.map((m) => (
                  <MerchantCard key={m.id} m={m} origin={origin} />
                ))}
              </div>
              <Pagination page={data} onPageChange={setPage} busy={loading} className="border-t-0 px-0" />
            </>
          ) : (
            <div className="rounded-md border bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTH {...thProps("dbaName")}>DBA</SortableTH>
                    <SortableTH {...thProps("mid")}>MID</SortableTH>
                    <SortableTH {...thProps("status")}>Status</SortableTH>
                    <SortableTH {...thProps("approvedDate")}>Approved</SortableTH>
                    <SortableTH {...thProps("ownerName")}>Owner</SortableTH>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Address</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Phone</TableHead>
                    <TableHead className="text-xs font-medium uppercase tracking-wide">Processor</TableHead>
                    <SortableTH {...thProps("lastVolume")} align="right">Last vol</SortableTH>
                    <SortableTH {...thProps("avgVolume")} align="right">Avg vol</SortableTH>
                    <TableHead className="text-right text-xs font-medium uppercase tracking-wide">Mo.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((m) => (
                    <TableRow
                      key={m.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/merchants/${m.id}`, { state: origin })}
                    >
                      <TableCell className="font-medium text-slate-900">
                        <Link to={`/merchants/${m.id}`} state={origin} className="hover:underline text-slate-900">
                          {m.dbaName}
                        </Link>
                        {m.legalName && m.legalName !== m.dbaName && (
                          <div className="text-xs text-slate-500">{m.legalName}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-700">{m.mid}</TableCell>
                      <TableCell>
                        <StatusPill value={m.status} map={MERCHANT_STATUS_TONES} />
                        {bucketOf(m.status) === "closed" && m.closedDate && (
                          <div className="text-xs text-red-600">Closed {formatDate(m.closedDate)}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums text-slate-700">
                        {formatDate(m.approvedDate) || <Dash />}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {m.owner.userNumber != null && <span className="text-slate-400">#{m.owner.userNumber} · </span>}
                        {m.owner.name || <Dash />}
                      </TableCell>
                      <TableCell className="text-slate-700">
                        {m.addressLine1 ? (
                          <>
                            <div>{m.addressLine1}</div>
                            <div className="text-xs text-slate-500">{cityLineOf(m) || <Dash />}</div>
                          </>
                        ) : (
                          cityLineOf(m) || <Dash />
                        )}
                      </TableCell>
                      <TableCell className="text-slate-700">{m.phone || <Dash />}</TableCell>
                      <TableCell className="text-slate-600">{m.lastProcessor || <Dash />}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-900">{m.lastVolume != null ? fmtUsd(m.lastVolume) : <Dash />}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-700">{m.avgVolume > 0 ? fmtUsd(m.avgVolume) : <Dash />}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-500">{m.statementCount || <Dash />}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={data} onPageChange={setPage} busy={loading} />
            </div>
          )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function bucketOf(status: string): "live" | "approved" | "inactive" | "closed" {
  const s = status.toLowerCase();
  if (s === "open" || s === "live" || s === "active") return "live";
  if (s === "approved") return "approved";
  if (s === "closed") return "closed";
  return "inactive";
}

const STATUS_TILES: { bucket: Bucket; label: string; dot: string; ring: string; tint: string }[] = [
  { bucket: "live", label: "Live", dot: "bg-emerald-500", ring: "ring-emerald-400", tint: "bg-emerald-50" },
  { bucket: "approved", label: "Approved", dot: "bg-blue-500", ring: "ring-blue-400", tint: "bg-blue-50" },
  { bucket: "inactive", label: "Inactive", dot: "bg-slate-400", ring: "ring-slate-400", tint: "bg-slate-50" },
  { bucket: "closed", label: "Closed", dot: "bg-red-500", ring: "ring-red-400", tint: "bg-red-50" },
];

function StatusTile({
  label, count, total, dot, ring, tint, active, onClick, title,
}: {
  label: string;
  count: number;
  total: number;
  dot: string;
  ring: string;
  tint: string;
  active: boolean;
  onClick?: () => void;
  title?: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const inner = (
    <>
      <div className="flex items-center gap-2">
        <span className={cn("h-2.5 w-2.5 rounded-full", dot)} />
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div className="text-3xl font-bold text-slate-900 mt-1 tabular-nums">{count.toLocaleString()}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{pct}% of book</div>
    </>
  );
  const base = "rounded-xl border bg-card p-4 text-left shadow-sm transition-colors";
  if (!onClick) {
    return <div className={cn(base, "cursor-default")} title={title}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(base, "hover:border-slate-300", active ? `ring-2 ${ring} ${tint}` : "")}
    >
      {inner}
    </button>
  );
}

function cityLineOf(m: MerchantListItem): string {
  return [m.city, [m.state, m.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

function MerchantCard({ m, origin }: { m: MerchantListItem; origin: BackOrigin }) {
  const bucket = bucketOf(m.status);
  const address = [m.addressLine1, cityLineOf(m)].filter(Boolean).join(", ");
  const midPending = /^pending[-_]/i.test(m.mid);
  return (
    <Link to={`/merchants/${m.id}`} state={origin} className="block h-full">
      <Card className="flex h-full flex-col p-4 transition-colors hover:border-slate-300">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 truncate font-semibold text-slate-900 leading-tight">{m.dbaName}</h3>
          <StatusPill value={m.status} map={MERCHANT_STATUS_TONES} className="shrink-0" />
        </div>
        <div className="mt-0.5">
          <Badge variant={midPending ? "warning" : "info"} className="max-w-full gap-1.5 rounded-md px-2 py-1 text-sm font-normal leading-4">
            <span className={midPending ? "text-amber-700/70" : "text-slate-500"}>Merchant ID:</span>
            <span className={cn("truncate font-mono font-medium", midPending ? "text-amber-800" : "text-slate-900")}>{m.mid}</span>
          </Badge>
        </div>

        <div className="mt-3 mb-4 space-y-1 text-sm leading-5">
          <div className="flex min-w-0 items-center gap-x-6 gap-y-1">
            <span className="flex min-w-0 items-center gap-1.5">
              <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
              {m.legalName ? (
                <span className="truncate text-slate-700">{m.legalName}</span>
              ) : (
                <span className="truncate text-slate-400">Legal name not provided</span>
              )}
            </span>
            {m.phone && (
              <span className="flex shrink-0 items-center gap-1.5 text-slate-700">
                <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                {m.phone}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
            {address ? (
              <span className="truncate text-slate-700">{address}</span>
            ) : (
              <span className="truncate text-slate-400">Address not provided</span>
            )}
          </div>
          {(m.lastVolume != null || m.lastProcessor) && (
            <div className="flex items-center gap-1.5">
              <CreditCard className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="truncate tabular-nums text-slate-700">
                {m.lastVolume != null ? fmtUsd(m.lastVolume) : <Dash />}
                <span className="text-slate-400">
                  {" "}last vol
                  {m.avgVolume > 0 && <> · avg {fmtUsd(m.avgVolume)}</>}
                  {m.statementCount > 0 && <> · {m.statementCount} mo</>}
                  {m.lastProcessor && <> · {m.lastProcessor}</>}
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-x-1.5 border-t pt-3 text-sm leading-5">
          <User className="h-4 w-4 shrink-0 text-slate-400" />
          {m.owner.userNumber != null && <span className="text-slate-400">#{m.owner.userNumber} ·</span>}
          <span className="font-semibold text-slate-900">{m.owner.name || <Dash />}</span>
          {bucket === "closed" && m.closedDate && (
            <span className="text-red-600">· Closed {formatDate(m.closedDate)}</span>
          )}
        </div>
      </Card>
    </Link>
  );
}

function fmtUsd(value: number): string {
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

